import arrayMutators from 'final-form-arrays';
import React, { useCallback, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { Checkbox, Title } from '@xsolla/ui-core';
import { Modal } from '@xsolla/ui-modal';

import { connectOAuthAuth } from '../../../api';
import { ChecklistItems } from '../../../api/configurations/checklist/types';
import {
  ClientTypes,
  OauthConnectResponse,
  OauthSettings
} from '../../../api/configurations/oauth2/types';
import { PROJECT_LINKS } from '../../../constants';
import { useChecklist, useCurrentProject } from '../../../contexts/settings';
import {
  CollapseWithTitle,
  FieldInput,
  FieldInputNumber,
  FormFooter,
  LinkFieldArray,
  LinkPAStyle,
  RadioGroup
} from '../../ui';
import { CommonFont, HeavyFont, ModalContentBox, ModalContentBoxToken, TokenExtra } from './style';
import { grantTypes } from './utils';
import { clientNameMaxLength, FormValues, Oauth2ModalValidator } from './validator';

type Props = {
  projectId: string;
  merchantId: string;
  onClose: () => void;
  onOpenSuccessModal: () => void;
  baseNamesNumber: number;
  setOauthSettings: React.Dispatch<React.SetStateAction<OauthSettings[]>>;
  setOAuthMainData: React.Dispatch<React.SetStateAction<OauthConnectResponse>>;
};

export const ConnectOauthModal = ({
  projectId,
  baseNamesNumber,
  merchantId,
  onClose,
  setOauthSettings,
  onOpenSuccessModal,
  setOAuthMainData
}: Props): JSX.Element => {
  const intl = useIntl();
  const { OAUTH2_WIDGET, OAUTH2API } = PROJECT_LINKS;

  const [fetching, setFetching] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [agreeLongToken, setAgreeLongToken] = useState<boolean>(false);

  const handleChangeCheck = useCallback(() => {
    setAgreeLongToken(!agreeLongToken);
  }, [agreeLongToken]);

  const { enableChecklistItem } = useChecklist(projectId);
  const { currentProject } = useCurrentProject(projectId);

  const onSubmit = async (values: FormValues) => {
    const isServer = values.is_public === ClientTypes.server;
    const isPublic = values.is_public === ClientTypes.public;
    const oauthClientAccess = isServer
      ? [
          {
            resource: values.merchant_project_id || merchantId,
            resource_name: !values.merchant_project_id ? 'publisher_id' : 'publisher_project_id'
          }
        ]
      : [];

    try {
      setFetching(true);
      setError(false);
      const newClient = {
        project_id: projectId,
        grant_types: grantTypes(isServer),
        scopes: [],
        ...values,
        redirect_uris:
          values.is_public === ClientTypes.server
            ? []
            : values.redirect_uris.filter((item) => !!item),
        is_public: isPublic,
        access: oauthClientAccess,
        lifetime: Number(values.lifetime),
        merchant_project_id: currentProject?.publisher_project_id
          ? currentProject.publisher_project_id
          : ''
      };

      const response = await connectOAuthAuth(newClient);
      await setOauthSettings((prev) => [
        ...prev,
        {
          name: values.name,
          project_id: projectId,
          redirect_uris: values.redirect_uris,
          secret: response.secret,
          response_types: ['code'],
          id: response.id,
          client_id: response.id,
          is_public: isPublic,
          grant_types: grantTypes(isServer),
          access: oauthClientAccess,
          lifetime: Number(values.lifetime)
        }
      ]);
      await enableChecklistItem(ChecklistItems.SET_UP_OAUTH);
      await setOAuthMainData(response);
      await onOpenSuccessModal();
      onClose();
    } catch (err) {
      setError(true);
    } finally {
      setFetching(false);
    }
  };

  const setDefaultName = () => {
    return baseNamesNumber
      ? `${intl.formatMessage({
          id: 'authorization.settings.oauth2.server.name.placeholder'
        })} (${baseNamesNumber + 1})`
      : intl.formatMessage({
          id: 'authorization.settings.oauth2.server.name.placeholder'
        });
  };

  return (
    <Modal onClose={onClose} size="md">
      <Title indent="lg">
        {intl.messages['authorization.settings.oauth2.modal.connect.title']}
      </Title>
      <ModalContentBox>
        <CollapseWithTitle
          title={intl.messages['authorization.settings.oauth2.modal.connect.instruction.title']}
          instruction={intl.formatMessage(
            {
              id: 'authorization.settings.oauth2.modal.connect.instruction'
            },
            {
              br: () => <br />,
              a: (chunk: string) => (
                <LinkPAStyle
                  href={OAUTH2_WIDGET}
                  target="_blank"
                  rel="noreferrer"
                  data-test-id="oauth2-widget-link">
                  {chunk}
                </LinkPAStyle>
              ),
              c: (chunk: string) => (
                <LinkPAStyle
                  href={OAUTH2API}
                  target="_blank"
                  rel="noreferrer"
                  data-test-id="oauth2-api-link">
                  {chunk}
                </LinkPAStyle>
              )
            }
          )}
        />
      </ModalContentBox>
      <Form
        keepDirtyOnReinitialize
        onSubmit={onSubmit}
        initialValues={{
          name: setDefaultName(),
          redirect_uris: [],
          is_public: ClientTypes.public,
          merchant_project_id: '',
          lifetime: 3600
        }}
        validate={Oauth2ModalValidator}
        mutators={{ ...arrayMutators }}>
        {({ handleSubmit, pristine, errors, values: { is_public, lifetime } }) => {
          return (
            <>
              <ModalContentBox>
                <FieldInput
                  label={intl.messages['authorization.settings.oauth2.table.client-name']}
                  errorMessage={
                    errors?.name &&
                    intl.formatMessage(
                      {
                        id: errors?.name
                      },
                      {
                        number: clientNameMaxLength
                      }
                    )
                  }
                  name="name"
                  placeholder={intl.formatMessage({
                    id: 'authorization.settings.oauth2.server.name.placeholder'
                  })}
                  data-test-id="oauth2-client-name"
                />
              </ModalContentBox>
              <Field name="is_public" type="radio">
                {(props) => (
                  <RadioGroup
                    name={props.input.name}
                    onChange={props.input.onChange}
                    defaultValue={ClientTypes.public}
                    options={[
                      {
                        label: intl.formatMessage(
                          {
                            id: 'authorization.settings.oauth2.modal.connect.type.public'
                          },
                          {
                            span: (chunk: string) => <CommonFont>{chunk}</CommonFont>
                          }
                        ),
                        radioValue: ClientTypes.public,
                        description: intl.formatMessage({
                          id: 'authorization.settings.oauth2.modal.connect.type.public.description'
                        }),
                        dataTestId: 'oauth2-type-public'
                      },
                      {
                        label: intl.formatMessage(
                          {
                            id: 'authorization.settings.oauth2.modal.connect.type.private'
                          },
                          {
                            span: (chunk: string) => <CommonFont>{chunk}</CommonFont>
                          }
                        ),
                        radioValue: ClientTypes.confidential,
                        description: intl.formatMessage({
                          id: 'authorization.settings.oauth2.modal.connect.type.confidential.description'
                        }),
                        dataTestId: 'oauth2-type-confidential'
                      },
                      {
                        label: intl.formatMessage(
                          {
                            id: 'authorization.settings.oauth2.modal.connect.type.server'
                          },
                          {
                            span: (chunk: string) => <CommonFont>{chunk}</CommonFont>
                          }
                        ),
                        radioValue: ClientTypes.server,
                        description: intl.formatMessage({
                          id: 'authorization.settings.oauth2.modal.connect.type.server.description'
                        }),
                        dataTestId: 'oauth2-type-server'
                      }
                    ]}
                  />
                )}
              </Field>
              {is_public !== ClientTypes.server && (
                <ModalContentBoxToken>
                  <LinkFieldArray
                    name="redirect_uris"
                    label={intl.formatMessage({
                      id: 'authorization.settings.oauth2.modal.connect.redirect-uri.title'
                    })}
                    addLabel={intl.formatMessage({
                      id: 'configuration-page.urls.callback-urls.input.add-button'
                    })}
                    placeholder={intl.formatMessage({
                      id: 'authorization.settings.oauth2.modal.connect.redirect-uri.placeholder'
                    })}
                  />
                </ModalContentBoxToken>
              )}
              <ModalContentBoxToken>
                <FieldInputNumber
                  name="lifetime"
                  hideArrows
                  subLabelCompact
                  label={intl.formatMessage({ id: 'settings.signature.token-lifetime.title' })}
                  errorMessage={
                    errors?.lifetime
                      ? intl.formatMessage(
                          { id: `${errors?.lifetime}` },
                          {
                            number:
                              `${errors?.lifetime}` === 'app.validation.value-more'
                                ? 1893456000
                                : 300
                          }
                        )
                      : ''
                  }
                  subLabel={
                    // eslint-disable-next-line react/jsx-wrap-multilines
                    <FormattedMessage
                      id="settings.signature.token-lifetime.description-oauth"
                      values={{
                        br: () => <br />
                      }}
                    />
                  }
                  extra={() => (
                    <TokenExtra>
                      {intl.formatMessage({ id: 'settings.signature.token-lifetime.seconds' })}
                    </TokenExtra>
                  )}
                  extraAlign="right"
                />
              </ModalContentBoxToken>
              {lifetime > 432000 && (
                <ModalContentBox>
                  <Checkbox
                    checked={agreeLongToken}
                    disabled={fetching}
                    onChange={handleChangeCheck}>
                    <HeavyFont>
                      {intl.formatMessage({ id: 'settings.signature.long-token-checkbox' })}
                    </HeavyFont>
                  </Checkbox>
                </ModalContentBox>
              )}

              <FormFooter
                isModal
                onSubmit={handleSubmit}
                onCancel={onClose}
                fetching={fetching}
                error={error}
                submitText={intl.formatMessage({
                  id: 'authorization.settings.oauth2.modal.connect.button'
                })}
                disabled={pristine || (lifetime > 432000 && !agreeLongToken)}
              />
            </>
          );
        }}
      </Form>
    </Modal>
  );
};
